/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SupportModule
 */

import {
    Inject,
    Injectable,
    OnDestroy,
} from '@angular/core';

import {
    FullModalService,
} from 'ng/modules/core/services/full-modal/full-modal.service';

import { L10nService } from '@vmw/ngx-vip';
import { AviNotificationType } from 'ng/modules/notification/avi-notification.types';

import {
    HttpMethod,
    HttpWrapper,
    THttpWrapper,
} from 'ajs/modules/core/factories/http-wrapper/http-wrapper.service';

import { BehaviorSubject, Observable } from 'rxjs';
import { Syserr } from 'generated-types';
import { NotificationService } from 'ng/modules/core/services/notification.service';
import { AsyncFactory } from 'ajs/modules/core/factories/async-factory/async.factory';
import { DevLoggerService } from 'ng/modules/core/services';
import * as l10n from './tech-support-generation.service.l10n';
import { ITechSupport, TechSupportFsmState } from '../../../../../generated-types/rest_dependents';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type TAsyncFactory = typeof AsyncFactory;

/**
 * @description Service for tracking Tech Support package progress.
 * @author Shahab Hashmi, Dnyaneshwar Jadhav, Dhanashree Tilak
 */

@Injectable({
    providedIn: 'root',
})
export class TechSupportGenerationService implements OnDestroy {
    /**
     * List of Packages in progress.
     */
    public readonly packagesInProgress$ = new BehaviorSubject<ITechSupport[]>([]);

    /**
     * Type of Tech Support.
     */
    public techSupportType: string;

    /**
     * Time taken for TS to complete.
     */
    public duration: string;

    /**
     * Resource name for TS.
     */
    public resourceName: string;

    /**
     * Show Output log based on TS type.
     */
    public outputLog: string;

    private readonly l10nKeys = l10nKeys;

    private readonly httpWrapper: HttpWrapper;

    private readonly isPollingCompleted$ = new BehaviorSubject<boolean>(false);

    private readonly recentTechSupport$ = new BehaviorSubject<ITechSupport>(null);

    /**
     * List of success codes.
     */
    private successCodes = new Set([
        Syserr.SYSERR_TECH_SUPPORT_COLLECTION_SUCCESS,
        Syserr.SYSERR_TECH_SUPPORT_COLLECTION_SUCCESS_WITH_ERRORS,
        Syserr.SYSERR_TECH_SUPPORT_COLLECTION_NOT_DONE,
    ]);

    /**
     * List of in progress codes.
     */
    private inProgressCodes = new Set([
        Syserr.SYSERR_TECH_SUPPORT_COLLECTION_STATUS_IN_PROGRESS,
        Syserr.SYSERR_TECH_SUPPORT_COLLECTION_IN_PROGRESS,
        Syserr.SYSERR_TECH_SUPPORT_COLLECTION_STARTED,
        Syserr.SYSERR_TECH_SUPPORT_COLLECTION_ONGOING,
    ]);

    /**
     * List of error codes.
     */
    private errorCodes = new Set([
        Syserr.SYSERR_TECH_SUPPORT_COLLECTION_ABORTED,
        Syserr.SYSERR_TECH_SUPPORT_INVALID_FILENAME,
        Syserr.SYSERR_TECH_SUPPORT_COLLECTION_STATUS_FILE_NOT_FOUND,
    ]);
    /**
     * Instance of AsyncFactory for in-progress package polling.
     */
    private pollingTechSupportPackages: AsyncFactory = null;

    constructor(
        private readonly fullModalService: FullModalService,
        private readonly l10nService: L10nService,
        private readonly notificationService: NotificationService,
        private devLoggerService: DevLoggerService,
        @Inject('AsyncFactory')
        private readonly AsyncFactory: TAsyncFactory,
        @Inject(HttpWrapper)
        HttpWrapper: THttpWrapper,
    ) {
        l10nService.registerSourceBundles(dictionary);
        this.httpWrapper = new HttpWrapper();
    }

    /** @override */
    public ngOnDestroy(): void {
        if (this.pollingTechSupportPackages) {
            this.pollingTechSupportPackages.stop();
        }

        this.packagesInProgress$.complete();
    }

    /**
     * Destroy Tech Support Polling status.
     */
    public stopTechSupportPackages(): void {
        if (this.pollingTechSupportPackages) {
            this.pollingTechSupportPackages.stop();
            this.pollingTechSupportPackages = null;
        }

        this.packagesInProgress$.next([]);
    }

    /**
     * Get status of Tech Support Generation.
     */
    public getTechSupportPackagesProgress = async(): Promise<ITechSupport[]> => {
        const url = 'api/techsupport';

        const requestConfig = {
            url,
            method: HttpMethod.GET,
        };

        try {
            const response = await this.httpWrapper.request(requestConfig);
            const allPackages = response.data.results || [];

            this.recentTechSupport$.next(this.extractRecentTechSupport(allPackages));

            const inProgressPackages = allPackages
                .filter((item: ITechSupport) => item.state.state === TechSupportFsmState.TECHSUPPORT_FSM_IN_PROGRESS);

            this.handleCompletedPackage(allPackages);

            return inProgressPackages;
        } catch (error: any) {
            this.devLoggerService.error(error);

            return [];
        }
    };

    /**
     * Return Recent Tech Support package from the tech support collection.
     */
    public extractRecentTechSupport(allPackages: ITechSupport[]): ITechSupport {
        return allPackages
            .filter((item: ITechSupport) => item.state.state !== TechSupportFsmState.TECHSUPPORT_FSM_IN_PROGRESS)
            .sort((a: ITechSupport, b: ITechSupport) => {
                return new Date(b.state.last_changed_time.secs).getTime() -
                    new Date(a.state.last_changed_time.secs).getTime();
            })[0];
    }

    /**
     * Launch new Tech Support generation process.
     */
    public async generateTechSupportPackage(): Promise<void> {
        await this.checkInprogressPackage();
    }

    /**
     * Check the process status based on the status code.
     */
    public checkInprogressPackage = (): void => {
        if (this.pollingTechSupportPackages) {
            return;
        }

        this.pollingTechSupportPackages = new this.AsyncFactory(async() => {
            const statusData = await this.getTechSupportPackagesProgress();

            if (statusData.length > 0) {
                this.packagesInProgress$.next(statusData);
            } else {
                this.packagesInProgress$.next([]);
                this.pollingTechSupportPackages.stop();
                this.pollingTechSupportPackages = null;
            }
        });
        this.pollingTechSupportPackages.start(10000);
    };

    /**
     * Return corresponding l10n value to Tech Support Type.
     */
    private getSupportTypeLabel = (type: string): string => {
        const {
            debugLogsLabel,
            clusteringLabel,
            metricsLogsLabel,
            gslbLabel,
            placementLabel,
            virtualServiceLabel,
            portalLabel,
            poolLabel,
            serviceEngineLabel,
            upgradeLabel,
        } = this.l10nKeys;

        switch (type) {
            case 'debuglogs':
                return debugLogsLabel;
            case 'clustering':
                return clusteringLabel;
            case 'metricslogs':
                return metricsLogsLabel;
            case 'gslb':
                return gslbLabel;
            case 'placement':
                return placementLabel;
            case 'upgrade':
                return upgradeLabel;
            case 'pool':
                return poolLabel;
            case 'virtualservice':
                return virtualServiceLabel;
            case 'serviceengine':
                return serviceEngineLabel;
            case 'portal':
                return portalLabel;
        }
    };

    /**
     * Handle success status.
     */
    public handleCompletedPackage(allPackages: ITechSupport[]): void {
        const currentPackages = this.packagesInProgress$.getValue();

        if (currentPackages.length && allPackages.length) {
            for (const pkg of currentPackages) {
                const pkgFound = allPackages.find(
                    (p: ITechSupport) => p.uuid === pkg.uuid,
                );

                if (pkgFound?.progress === 100) {
                    this.outputLog = pkgFound.output?.split('/').pop() || '';
                    this.isPollingCompleted$.next(true);

                    if (pkg.level) {
                        this.notificationService.addSimpleNotification(
                            this.generateNotificationMessage(pkg.level),
                            this.outputLog,
                            AviNotificationType.SUCCESS,
                        );
                    }
                }
            }
        }
    }

    /**
     * Return final message to be displayed as Notification.
     */
    private generateNotificationMessage = (type: string): string => {
        const {
            l10nService,
            l10nKeys,
            getSupportTypeLabel,
        } = this;

        const {
            collectionCompleteLabel,
            techSupportLabel,
        } = l10nKeys;

        const supportTypeLabel = getSupportTypeLabel(type);
        const ccLabel = l10nService.getMessage(collectionCompleteLabel);
        const tsLabel = l10nService.getMessage(techSupportLabel, [ccLabel]);

        return l10nService.getMessage(supportTypeLabel, [tsLabel]);
    };

    /**
     * Returns polling process completed status for reloading grid data.
     */
    public getPollingProcessStatus(): Observable<boolean> {
        return this.isPollingCompleted$;
    }

    /**
     * Get a single Tech Support Package by UUID.
     * Return tech support package object or null if not found.
     */
    public async getTechSupportPackage(uuid: string): Promise<ITechSupport | null> {
        const url = `api/techsupport/${uuid}`;
        const requestConfig = {
            url,
            method: HttpMethod.GET,
        };

        try {
            const response = await this.httpWrapper.request(requestConfig);

            return response.data ?? null;
        } catch (error: any) {
            this.devLoggerService.error(`Failed to fetch tech support package with UUID: ${uuid}`, error);

            return null;
        }
    }

    /**
     * Return recent tech support package.
     */
    public getRecentTechSupport(): Observable<ITechSupport> {
        return this.recentTechSupport$;
    }

    /**
     * Format seconds as hh:mm:ss.
     */
    public formatDuration(durationInSeconds: number): string {
        if (!durationInSeconds) {
            return '-';
        }

        const hours = Math.floor(durationInSeconds / 3600);
        const minutes = Math.floor(durationInSeconds % 3600 / 60);
        const seconds = durationInSeconds % 60;

        // eslint-disable-next-line max-len
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
}
